/**
 * withBlockRegistration HOC
 * --------------------------------------------------------------------------
 * Wrap your block component with this HOC to automatically:
 *   1. Register/unregister itself in the Block Registry (when provider present)
 *   2. Attach `id="block-<id>"` to the root element for public-site scroll
 *      fall-back.
 *
 * Usage:
 * ```ts
 * export default withBlockRegistration(MyBlock, 'hero');
 * ```
 */

import React, { useEffect, useRef } from 'react';
import { useBlockRegistry } from './BlockRegistryContext';

interface WithBlockRegistrationProps {
    id?: string | number;
    heading?: string;
    title?: string;
    [key: string]: any;
}

/**
 * Higher-order component that registers a block with the BlockRegistry
 * This wrapper is only used in the page builder context
 */
export function withBlockRegistration<P extends WithBlockRegistrationProps>(WrappedComponent: React.ComponentType<P>, blockType: string) {
    return React.forwardRef<HTMLElement, P>((props, ref) => {
        const { id, heading, title, ...restProps } = props;
        const localRef = useRef<HTMLElement>(null);
        const finalRef = (ref || localRef) as React.RefObject<HTMLElement>;

        // Try to use the registry if available
        let registry: ReturnType<typeof useBlockRegistry> | null = null;
        try {
            registry = useBlockRegistry();
        } catch {
            // Registry not available - just render the component
        }

        useEffect(() => {
            // Ensure the underlying element has #block-{id} for DOM-based scrolling (public site)
            if (id && finalRef.current) {
                finalRef.current.setAttribute('id', `block-${id}`);
            }

            if (id && registry) {
                const label = heading || title || `${blockType} Block`;

                registry.register(id, {
                    ref: finalRef,
                    type: blockType,
                    label,
                    callbacks: {
                        // Add any default callbacks here
                    },
                });

                return () => {
                    registry.unregister(id);
                };
            }
        }, [id, registry, heading, title]); // Note: callbacks are not in deps to avoid re-registration

        // Pass through all props including ref
        const allProps = { ...restProps, id, heading, title } as unknown as P;
        return <WrappedComponent ref={finalRef} {...allProps} />;
    });
}
